<template>
  <a-modal
    width="800px"
    :visible="true"
    :maskClosable="false"
    :confirmLoading="loading"
    cancelText="取消"
    okText="确定上传"
    centered
    @ok="handleSubmit"
    @cancel="
      () => {
        // resetFields();
        $emit('cancel');
      }
    "
  >
    <template #title>
      <div ref="modalTitleRef" style="width: 100%; cursor: move">{{ title }}</div>
    </template>
    <template #modalRender="{ originVNode }">
      <div :style="transformStyle">
        <component :is="originVNode" />
      </div>
    </template>
    <a-upload
      :customRequest="customRequest"
      :multiple="multiple"
      v-model:file-list="fileList"
      list-type="picture-card"
      :remove="handleRemove"
      @preview="handlePreview"
    >
      <plus-outlined />
      <div class="ant-upload-text">上传</div>
    </a-upload>
  </a-modal>
  <a-modal :visible="previewVisible" :footer="null" @cancel="handleCancel" :maskClosable="false">
    <img alt="example" style="width: 100%" :src="previewImage" />
  </a-modal>
</template>

<script lang="ts">
import { PlusOutlined } from '@ant-design/icons-vue';
import { computed, defineComponent, CSSProperties, ref, watch, watchEffect } from 'vue';
import { useDraggable } from '@vueuse/core';
import { get_upload_files_url, upload_file } from '@/api/nsbcs-modal';
import { message } from 'ant-design-vue';

const permissionActions = ['add', 'delete', 'update', 'query', 'import', 'export'];
function getBase64(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
}

interface FileItem {
  uid: string;
  fid?: number;
  name?: string;
  status?: string;
  response?: Response;
  percent?: number;
  url: string;
  preview?: string;
  originFileObj?: any;
}

export default defineComponent({
  name: 'UploadImageModal',
  components: { PlusOutlined },
  props: {
    title: {
      type: String,
      required: true,
    },
    group: {
      type: String,
      required: true,
      default: () => 'sys',
    },
    access_type: {
      type: String,
      required: true,
      default: () => 'public',
    },

    loading: {
      type: Boolean,
    },
    multiple: {
      type: Boolean,
      default: () => false,
    },
  },
  emits: ['ok', 'cancel'],
  setup(props: any, { emit }: any) {
    const previewVisible = ref<boolean>(false);
    const previewImage = ref<string | undefined>('');
    const modalTitleRef: any = ref(null);
    const { x, y, isDragging } = useDraggable(modalTitleRef);
    const handleCancel = () => {
      previewVisible.value = false;
    };
    const handlePreview = async (file: FileItem) => {
      if (!file.url && !file.preview) {
        file.preview = (await getBase64(file.originFileObj)) as string;
      }
      previewImage.value = file.url || file.preview;
      previewVisible.value = true;
    };
    const fileList = ref<FileItem[]>([]);
    const extname = (filename: any) => {
      if (!filename || typeof filename != 'string') {
        return false;
      }
      var a = filename.split('').reverse().join('');
      var b = a.substring(0, a.search(/\./)).split('').reverse().join('');
      return b;
    };
    const file_ids: number[] = [];
    const customRequest = (fileobj: any) => {
      let type = extname(fileobj.file.name);
      console.log('fileList', fileList);
      get_upload_files_url({
        access_type: props.access_type,
        filetype: type,
        filename: fileobj.file.name,
        group: props.group,
        author: 0,
      }).then((res: any) => {
        let formData = new FormData();
        formData.append('file', fileobj.file);
        formData.append('file_id', res.params.file_id);
        formData.append('key', res.params.key);
        upload_file(formData, {
          onUploadProgress: (progressEvent: any) => {
            let complete = ((progressEvent.loaded / progressEvent.total) * 100) | 0;
            fileobj.onProgress({ percent: complete });
          },
        }).then((res1: any) => {
          console.log('res1', res1);
          file_ids.push(res.params.file_id);
          fileobj.file.fid = res.params.file_id;
          fileobj.file.url = res.params.url;
          fileobj.onSuccess();
          message.success('创建成功，可以【确认上传】');
        });
      });
    };
    const startX = ref<number>(0);
    const startY = ref<number>(0);
    const startedDrag = ref(false);
    const transformX = ref(0);
    const transformY = ref(0);
    const preTransformX = ref(0);
    const preTransformY = ref(0);
    const dragRect = ref({ left: 0, right: 0, top: 0, bottom: 0 });
    watch([x, y], () => {
      if (!startedDrag.value) {
        startX.value = x.value;
        startY.value = y.value;
        const bodyRect = document.body.getBoundingClientRect();
        const titleRect = modalTitleRef.value.getBoundingClientRect();
        dragRect.value.right = bodyRect.width - titleRect.width;
        dragRect.value.bottom = bodyRect.height - titleRect.height;
        preTransformX.value = transformX.value;
        preTransformY.value = transformY.value;
      }
      startedDrag.value = true;
    });
    watch(isDragging, () => {
      if (!isDragging) {
        startedDrag.value = false;
      }
    });
    watchEffect(() => {
      if (startedDrag.value) {
        transformX.value =
          preTransformX.value +
          Math.min(Math.max(dragRect.value.left, x.value), dragRect.value.right) -
          startX.value;
        transformY.value =
          preTransformY.value +
          Math.min(Math.max(dragRect.value.top, y.value), dragRect.value.bottom) -
          startY.value;
      }
    });
    const transformStyle = computed<CSSProperties>(() => {
      return {
        transform: `translate(${transformX.value}px, ${transformY.value}px)`,
      };
    });

    const handleSubmit = (e: Event) => {
      e.preventDefault();
      if (file_ids.length == 0) {
        message.success('请选择需要上传的附件');
        return;
      }
      emit('ok', file_ids);
    };

    const handleRemove = (file: any) => {
      file_ids.splice(file_ids.indexOf(file.originFileObj.fid), 1);
    };
    return {
      props,
      handleRemove,
      handleSubmit,
      customRequest,
      previewVisible,
      previewImage,
      handleCancel,
      handlePreview,
      modalTitleRef,
      transformStyle,
      fileList,
      permissionActions,
    };
  },
});
</script>
